import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Plus, Trash2, Clock, MapPin, Users, TrendingUp, TrendingDown, Briefcase, Edit, Copy, List, Grid, AlertTriangle, CheckCircle, Search, X } from 'lucide-react';
import { API_URL } from '../config';

// Réplique exacte de la logique backend getMonthlyContractHours()
const getMonthlyContractHours = (heuresHebdo) => {
  const mapping = { 35: 151.67, 39: 169.00, 40: 173.33 };
  return mapping[heuresHebdo] || 151.67;
};

// Formater les heures en format +Xh00 / -Xh00 (réplique de ModulationManagement)
const formatHeuresModulation = (heures) => {
  if (heures === null || heures === undefined || isNaN(heures)) return '—';
  const h = Math.floor(Math.abs(heures));
  const m = Math.round((Math.abs(heures) - h) * 60);
  const sign = heures >= 0 ? '+' : '-';
  return `${sign}${h}h${m.toString().padStart(2, '0')}`;
};

// Couleur selon le delta de modulation
const getModulationColor = (delta) => {
  if (delta > 0) return 'text-green-600';
  if (delta < 0) return 'text-red-600';
  return 'text-text-secondary';
};

// Couleur de fond selon le delta de modulation
const getModulationBgColor = (delta) => {
  if (delta > 0) return 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800';
  if (delta < 0) return 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800';
  return 'bg-elevated-1 border-border-subtle';
};

// Seuil d'alerte : si |delta| dépasse 10% des heures mensuelles contractuelles
const ALERT_THRESHOLD_PERCENT = 0.10;

// Déterminer le niveau d'alerte mensuel
const getMonthlyAlert = (modulationDelta, heuresContratMensuel) => {
  if (heuresContratMensuel <= 0 || modulationDelta === null || isNaN(modulationDelta)) return null;
  const threshold = heuresContratMensuel * ALERT_THRESHOLD_PERCENT;
  if (modulationDelta < -threshold) {
    return { level: 'danger', message: 'Heures à rattraper', icon: '🔴' };
  }
  if (modulationDelta > threshold) {
    return { level: 'warning', message: 'Heures à récupérer', icon: '🟠' };
  }
  return { level: 'ok', message: 'Dans la norme', icon: '🟢' };
};

const Planning = ({ employees = [], pointages = [], authUser = null }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Pour forcer le recalcul des stats
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [draggedSchedule, setDraggedSchedule] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'weekly'
  const [showExtrasPlanning, setShowExtrasPlanning] = useState(false);
  const [searchFilterPlanning, setSearchFilterPlanning] = useState('');
  const [serviceFilterPlanning, setServiceFilterPlanning] = useState('');
  const [personalViewOnly, setPersonalViewOnly] = useState(false);
  const [showStatsSection, setShowStatsSection] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '',
    date: '',
    start_time: '',
    end_time: '',
    entry_type: 'Travail',
    location: '',
    title: '',
    description: '',
    recurrence: 'none',
    recurrence_end_date: '',
    degustation_menu: '',
    degustation_cuisinier_id: '',
    conge_type: '',
    recuperation_hours: ''
  });
  const [modulationSolde, setModulationSolde] = useState(null);
  const [soldeCp, setSoldeCp] = useState(null);

  // Définir la vue par défaut selon le rôle
  useEffect(() => {
    if (authUser && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name)) {
      setViewMode('weekly'); // Vue hebdomadaire par défaut pour les managers
    } else {
      setViewMode('calendar'); // Vue calendrier par défaut pour les employés
    }
  }, [authUser]);

  // Filtrer les employés selon le rôle de l'utilisateur connecté + filtres actifs
  const getVisibleEmployees = () => {
    if (!authUser) return employees;

    const userRole = authUser.role_name;

    // Mode "Mon planning" : ne voir que soi-même
    if (personalViewOnly) {
      return employees.filter(emp => emp.id === authUser.id || emp.id === authUser.employee_id);
    }

    // Employé : voir seulement lui-même (pas de filtres supplémentaires)
    if (userRole === 'Employé') {
      return employees.filter(emp => emp.id === authUser.id || emp.id === authUser.employee_id);
    }

    let base = [];

    // Patron et Super Manager : voir tout le monde (Extras selon toggle)
    if (userRole === 'Patron' || userRole === 'Super Manager') {
      base = showExtrasPlanning
        ? employees
        : employees.filter(emp => emp.role_name !== 'Extra');
    } else if (userRole === 'Manager') {
      // Manager : voir seulement son service
      base = employees.filter(emp => emp.service_id === authUser.service_id);
    } else {
      return employees;
    }

    // Appliquer la recherche texte
    if (searchFilterPlanning) {
      const s = searchFilterPlanning.toLowerCase();
      base = base.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(s) ||
        (emp.service_name || '').toLowerCase().includes(s)
      );
    }

    // Appliquer le filtre service
    if (serviceFilterPlanning) {
      base = base.filter(emp => emp.service_name === serviceFilterPlanning);
    }

    return base;
  };

  const visibleEmployees = getVisibleEmployees();

  // Fonction pour trier les employés selon le rôle de l'utilisateur connecté
  const getSortedEmployeesForWeeklyView = () => {
    if (!authUser) return visibleEmployees;

    const myEmpId = authUser.employee_id || authUser.id;
    const sortedEmployees = [...visibleEmployees];

    return sortedEmployees.sort((a, b) => {
      // L'utilisateur connecté TOUJOURS en première ligne
      const aIsMe = a.id === myEmpId;
      const bIsMe = b.id === myEmpId;
      if (aIsMe && !bIsMe) return -1;
      if (!aIsMe && bIsMe) return 1;

      // Ensuite tri par hiérarchie de rôle
      const roleOrder = { 'Patron': 1, 'Super Manager': 2, 'Manager': 3, 'Employé': 4, 'Extra': 5 };
      const aOrder = roleOrder[a.role_name] || 4;
      const bOrder = roleOrder[b.role_name] || 4;
      if (aOrder !== bOrder) return aOrder - bOrder;

      // Puis alphabétique
      return (a.first_name || '').localeCompare(b.first_name || '');
    });
  };

  // Forcer le recalcul des statistiques quand les pointages changent
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [pointages]);

  // Fonction utilitaire pour formater une date en YYYY-MM-DD sans décalage UTC
  const formatDateLocal = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Charger les plannings (marge de 7 jours pour couvrir le chevauchement hebdomadaire)
  const loadSchedules = async () => {
    setLoading(true);
    try {
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1 - 7);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0 + 7);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${API_URL}/schedules?start_date=${formatDateLocal(startDate)}&end_date=${formatDateLocal(endDate)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement planning:', error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les tâches par rôle (même logique que Taches.jsx)
  const filterTasksByRole = (allTasks) => {
    if (!authUser) return [];
    const role = authUser.role_name;
    if (['Patron', 'Super Manager'].includes(role)) return allTasks;
    const userId = parseInt(authUser.employee_id || authUser.id);
    const userServiceId = parseInt(authUser.service_id);
    if (role === 'Manager') {
      const svcEmpIds = employees
        .filter(e => parseInt(e.service_id) === userServiceId)
        .map(e => e.id);
      return allTasks.filter(t =>
        svcEmpIds.includes(parseInt(t.assigned_to)) ||
        parseInt(t.assigned_by) === userId ||
        (userServiceId && parseInt(t.assigned_service_id) === userServiceId)
      );
    }
    // Employé / Extra
    return allTasks.filter(t =>
      parseInt(t.assigned_to) === userId ||
      (userServiceId && parseInt(t.assigned_service_id) === userServiceId)
    );
  };

  // Charger les tâches avec date limite
  const loadTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Filtrer par rôle, puis uniquement les tâches qui ont une date (limite ou planifiée)
      const roleFiltered = filterTasksByRole(Array.isArray(data) ? data : []);
      setTasks(roleFiltered.filter(task => task.due_date || task.scheduled_date));
    } catch (error) {
      console.error('Erreur chargement tâches:', error);
      setTasks([]);
    }
  };

  useEffect(() => {
    loadSchedules();
    loadTasks();
  }, [currentDate, authUser, employees]);

  // Écouter l'événement de rechargement des schedules (depuis Congés)
  useEffect(() => {
    const handleReloadSchedules = () => {
      loadSchedules();
    };

    window.addEventListener('reloadSchedules', handleReloadSchedules);
    return () => window.removeEventListener('reloadSchedules', handleReloadSchedules);
  }, []);

  // Charger le solde modulation et CP quand l'employé sélectionné change
  useEffect(() => {
    const empId = formData.employee_id;
    if (!empId) { setModulationSolde(null); setSoldeCp(null); return; }
    const token = localStorage.getItem('token');
    const now = new Date();

    // Modulation
    fetch(`${API_URL}/modulation/${empId}?year=${now.getFullYear()}&month=${now.getMonth() + 1}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : null).then(data => {
      if (data && data.compteurTotal !== undefined) setModulationSolde(data.compteurTotal);
      else setModulationSolde(null);
    }).catch(() => setModulationSolde(null));

    // Solde CP
    fetch(`${API_URL}/conges-solde/${empId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : null).then(data => {
      if (data && data.restant !== undefined) setSoldeCp(data.restant);
      else setSoldeCp(null);
    }).catch(() => setSoldeCp(null));
  }, [formData.employee_id]);

  // Calculer les heures estimées d'une entrée de planning
  const calculateEstimatedHours = (startTime, endTime, entryType) => {
    if (!startTime || !endTime) return 0;

    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;
    // Gérer le passage de minuit : si fin < début, l'événement déborde sur le jour suivant
    // (cas d'égalité = saisie incohérente, on garde 0h)
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }
    let duration = (endMinutes - startMinutes) / 60;

    // Appliquer la pause automatique si > 4h (à partir du 27/03/2026)
    if ((entryType === 'Travail' || entryType === 'Télétravail') && duration > 4) {
      duration -= 0.75; // Déduction de 45min
    }
    if (entryType === 'Formation' && duration > 4) {
      duration -= 1; // Déduction de 1h pour Formation
    }
    
    return Math.max(0, duration);
  };

  // Calculer les statistiques de temps pour un employé
  const getEmployeeStats = (employeeId) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Obtenir le premier jour de la semaine actuelle (lundi)
    const firstDayOfWeek = new Date(now);
    const dayOfWeek = firstDayOfWeek.getDay();
    const diff = firstDayOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    firstDayOfWeek.setDate(diff);
    firstDayOfWeek.setHours(0, 0, 0, 0);
    
    const lastDayOfWeek = new Date(firstDayOfWeek);
    lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
    lastDayOfWeek.setHours(23, 59, 59, 999);
    
    // Filtrer les entrées de cet employé
    const employeeSchedules = schedules.filter(s => s.employee_id === employeeId);
    
    // Calculer les heures ESTIMÉES de la semaine courante
    const weekSchedules = employeeSchedules.filter(s => {
      const scheduleDate = new Date(s.date);
      return scheduleDate >= firstDayOfWeek && scheduleDate <= lastDayOfWeek;
    });
    
    const weekEstimatedHours = weekSchedules.reduce((total, schedule) => {
      // Ne compter que les types comptabilisés en heures
      if (['Travail', 'Télétravail', 'Événement', 'Formation', 'Dégustation'].includes(schedule.entry_type || schedule.shift_type)) {
        return total + calculateEstimatedHours(schedule.start_time, schedule.end_time, schedule.entry_type || schedule.shift_type);
      }
      return total;
    }, 0);
    
    // Calculer les heures RÉELLES de la semaine courante depuis les pointages
    const weekRealHours = pointages.reduce((total, pointage) => {
      if ((pointage.employee_id === employeeId || pointage.user_id === employeeId) && 
          pointage.statut === 'termine' && 
          pointage.heures_travaillees) {
        const pointageDate = new Date(pointage.date);
        if (pointageDate >= firstDayOfWeek && pointageDate <= lastDayOfWeek) {
          return total + parseFloat(pointage.heures_travaillees);
        }
      }
      return total;
    }, 0);
    
    // Calculer les heures ESTIMÉES du mois courant
    const monthSchedules = employeeSchedules.filter(s => {
      const scheduleDate = new Date(s.date);
      return scheduleDate.getFullYear() === currentYear && scheduleDate.getMonth() === currentMonth;
    });
    
    const monthEstimatedHours = monthSchedules.reduce((total, schedule) => {
      // Ne compter que Travail, Télétravail, Événement et Formation
      if (['Travail', 'Télétravail', 'Événement', 'Formation'].includes(schedule.entry_type || schedule.shift_type)) {
        return total + calculateEstimatedHours(schedule.start_time, schedule.end_time, schedule.entry_type || schedule.shift_type);
      }
      return total;
    }, 0);
    
    // Calculer les heures RÉELLES du mois courant depuis les pointages
    const monthRealHours = pointages.reduce((total, pointage) => {
      if ((pointage.employee_id === employeeId || pointage.user_id === employeeId) && 
          pointage.statut === 'termine' && 
          pointage.heures_travaillees) {
        const pointageDate = new Date(pointage.date);
        if (pointageDate.getFullYear() === currentYear && pointageDate.getMonth() === currentMonth) {
          return total + parseFloat(pointage.heures_travaillees);
        }
      }
      return total;
    }, 0);
    
    // Calcul de la modulation mensuelle (réplique du calcul backend)
    const emp = employees.find(e => e.id === employeeId);
    const heuresHebdo = emp?.heures_hebdomadaires || null;
    const heuresContratMensuel = heuresHebdo ? getMonthlyContractHours(heuresHebdo) : null;
    // soldeMensuel = heures réellement pointées - heures attendues par contrat
    const modulationMensuelle = (heuresContratMensuel !== null) ? (monthRealHours - heuresContratMensuel) : null;
    // Alerte basée sur le seuil mensuel
    const alerteMensuelle = (heuresContratMensuel !== null) ? getMonthlyAlert(modulationMensuelle, heuresContratMensuel) : null;

    return {
      weekEstimatedHours: weekEstimatedHours.toFixed(2),
      weekRealHours: weekRealHours.toFixed(2),
      monthEstimatedHours: monthEstimatedHours.toFixed(2),
      monthRealHours: monthRealHours.toFixed(2),
      heuresContratMensuel,
      modulationMensuelle,
      alerteMensuelle
    };
  };

  // Générer les jours du mois
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getSchedulesForDay = (date) => {
    if (!date) return [];
    const dateStr = formatDateLocal(date);
    const daySchedules = schedules.filter(s => s.date === dateStr);

    // Filtrer selon les permissions de l'utilisateur
    const visibleEmployeeIds = visibleEmployees.map(e => e.id);
    const baseSchedules = daySchedules.filter(s => visibleEmployeeIds.includes(s.employee_id));

    // Ajouter les dégustations où un employé visible est cuisinier (entrée supplémentaire)
    const cuisinierEntries = daySchedules.filter(s =>
      s.entry_type === 'Dégustation' &&
      s.degustation_cuisinier_id &&
      visibleEmployeeIds.includes(parseInt(s.degustation_cuisinier_id)) &&
      s.employee_id !== parseInt(s.degustation_cuisinier_id)
    ).map(s => ({ ...s, _isCuisinier: true }));

    return [...baseSchedules, ...cuisinierEntries];
  };

  const getTasksForDay = (date) => {
    if (!date) return [];
    const dateStr = formatDateLocal(date);
    return tasks.filter(t => t.scheduled_date === dateStr || (!t.scheduled_date && t.due_date === dateStr));
  };

  // Récupérer les heures travaillées pour un employé à une date donnée
  const getWorkedHoursForEmployee = (employeeId, date) => {
    if (!date || !pointages.length) return null;
    const dateStr = formatDateLocal(date);

    // Chercher le pointage pour cet employé à cette date
    const pointage = pointages.find(p => {
      const match = (p.user_id === employeeId || p.employee_id === employeeId) &&
                    p.date === dateStr &&
                    p.statut === 'termine';
      return match;
    });

    if (pointage && pointage.heures_travaillees) {
      return parseFloat(pointage.heures_travaillees);
    }

    return null;
  };

  // Obtenir la semaine courante (lundi à dimanche)
  const getCurrentWeek = () => {
    const date = new Date(currentDate);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Ajuster au lundi
    const monday = new Date(date.setDate(diff));
    
    const week = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      week.push(day);
    }
    return week;
  };

  // Naviguer semaine par semaine
  const previousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  // Obtenir toutes les entrées pour un employé sur une semaine
  const getWeekSchedulesForEmployee = (employeeId) => {
    const week = getCurrentWeek();
    const weekSchedules = {};

    week.forEach(date => {
      const dateStr = formatDateLocal(date);
      const mySchedules = schedules.filter(s =>
        s.employee_id === employeeId && s.date === dateStr
      );
      // Dégustations où cet employé est le cuisinier (mais pas déjà dans mySchedules)
      const cuisinierDegus = schedules.filter(s =>
        s.entry_type === 'Dégustation' &&
        parseInt(s.degustation_cuisinier_id) === employeeId &&
        s.employee_id !== employeeId &&
        s.date === dateStr
      ).map(s => ({ ...s, _isCuisinier: true }));
      weekSchedules[dateStr] = [...mySchedules, ...cuisinierDegus];
    });

    return weekSchedules;
  };

  // Calculer le total d'heures pointées et planifiées pour la semaine
  const getWeekSummaryForEmployee = (employeeId) => {
    const week = getCurrentWeek();
    let totalPointedHours = 0;
    let totalPlannedHours = 0;
    
    week.forEach(date => {
      const dateStr = formatDateLocal(date);
      
      // Heures pointées
      const workedHours = getWorkedHoursForEmployee(employeeId, date);
      if (workedHours !== null) {
        totalPointedHours += workedHours;
      }
      
      // Heures planifiées
      const daySchedules = schedules.filter(s => 
        s.employee_id === employeeId && 
        s.date === dateStr &&
        ['Travail', 'Télétravail', 'Événement', 'Formation'].includes(s.entry_type || s.shift_type)
      );
      
      daySchedules.forEach(schedule => {
        totalPlannedHours += calculateEstimatedHours(
          schedule.start_time, 
          schedule.end_time, 
          schedule.entry_type || schedule.shift_type
        );
      });
    });
    
    return { totalPointedHours, totalPlannedHours };
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Générer les dates selon la récurrence
      const datesToCreate = [];
      // Forcer l'heure locale pour éviter le décalage UTC
      const startDate = new Date(formData.date + 'T00:00:00');

      if (formData.recurrence === 'none') {
        // Entrée unique
        datesToCreate.push(formData.date);
      } else {
        // Entrées récurrentes
        const endDate = formData.recurrence_end_date
          ? new Date(formData.recurrence_end_date + 'T00:00:00')
          : new Date(startDate);
        if (!formData.recurrence_end_date) endDate.setMonth(endDate.getMonth() + 3);
        
        let currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
          if (formData.recurrence === 'working_week') {
            // N'ajouter que les jours Lun-Ven
            const dow = currentDate.getDay();
            if (dow >= 1 && dow <= 5) {
              datesToCreate.push(formatDateLocal(currentDate));
            }
            currentDate.setDate(currentDate.getDate() + 1);
          } else {
            datesToCreate.push(formatDateLocal(currentDate));
            // Incrémenter selon le type de récurrence
            if (formData.recurrence === 'daily') {
              currentDate.setDate(currentDate.getDate() + 1);
            } else if (formData.recurrence === 'weekly') {
              currentDate.setDate(currentDate.getDate() + 7);
            } else if (formData.recurrence === 'monthly') {
              currentDate.setMonth(currentDate.getMonth() + 1);
            }
          }

          // Limite de sécurité : max 365 entrées
          if (datesToCreate.length >= 365) break;
        }
      }
      
      // Créer toutes les entrées
      const token = localStorage.getItem('token');
      const createPromises = datesToCreate.map(date =>
        fetch(`${API_URL}/schedules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            ...formData,
            date,
            recurrence: undefined,
            recurrence_end_date: undefined,
            recuperation_hours: formData.recuperation_hours && formData.recuperation_hours !== 'custom' ? parseFloat(formData.recuperation_hours) : undefined
          })
        }).then(async r => {
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error(err.error || 'Erreur lors de la création');
          }
          return r.json();
        })
      );
      
      await Promise.all(createPromises);
      await loadSchedules();
      setShowAddModal(false);
      setFormData({
        employee_id: '',
        date: '',
        start_time: '',
        end_time: '',
        entry_type: 'Travail',
        location: '',
        title: '',
        description: '',
        recurrence: 'none',
        recurrence_end_date: '',
        degustation_menu: '',
        degustation_cuisinier_id: '',
        conge_type: '',
        recuperation_hours: ''
      });
    } catch (error) {
      console.error('Erreur ajout planning:', error);
      alert(error.message || 'Erreur lors de la création du planning');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/schedules/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadSchedules();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  // Ouvrir le modal d'édition
  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      employee_id: schedule.employee_id,
      date: schedule.date,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      entry_type: schedule.entry_type || schedule.shift_type || 'Travail',
      location: schedule.location || '',
      title: schedule.title || '',
      description: schedule.description || '',
      recurrence: 'none',
      recurrence_end_date: '',
      degustation_menu: schedule.degustation_menu || '',
      degustation_cuisinier_id: schedule.degustation_cuisinier_id || '',
      conge_type: schedule.conge_type || '',
      recuperation_hours: schedule.recuperation_hours || ''
    });
    setShowAddModal(true);
  };

  // Mettre à jour une entrée existante
  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/schedules/${editingSchedule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          employee_id: formData.employee_id,
          date: formData.date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          entry_type: formData.entry_type,
          location: formData.location,
          title: formData.title,
          description: formData.description,
          conge_type: formData.conge_type || null,
          recuperation_hours: formData.recuperation_hours ? parseFloat(formData.recuperation_hours) : null
        })
      });
      await loadSchedules();
      setShowAddModal(false);
      setEditingSchedule(null);
      setFormData({
        employee_id: '',
        date: '',
        start_time: '',
        end_time: '',
        entry_type: 'Travail',
        location: '',
        title: '',
        description: '',
        recurrence: 'none',
        recurrence_end_date: '',
        conge_type: '',
        recuperation_hours: ''
      });
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dupliquer une entrée (sur le même jour)
  const handleDuplicateSchedule = async (schedule) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          employee_id: schedule.employee_id,
          date: schedule.date,
          start_time: schedule.start_time,
          end_time: schedule.end_time,
          entry_type: schedule.entry_type || schedule.shift_type || 'Travail',
          location: schedule.location || '',
          title: schedule.title || '',
          description: schedule.description || '',
          degustation_menu: schedule.degustation_menu || null,
          degustation_cuisinier_id: schedule.degustation_cuisinier_id || null
        })
      });
      await loadSchedules();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
    } finally {
      setLoading(false);
    }
  };

  // Drag & Drop - Début du drag
  const handleDragStart = (e, schedule) => {
    setDraggedSchedule(schedule);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  // Drag & Drop - Survol d'une date
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Drag & Drop - Déposer sur une nouvelle date
  const handleDrop = async (e, newDate) => {
    e.preventDefault();
    if (!draggedSchedule) return;

    const newDateStr = formatDateLocal(newDate);
    if (newDateStr === draggedSchedule.date) {
      setDraggedSchedule(null);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/schedules/${draggedSchedule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...draggedSchedule,
          date: newDateStr
        })
      });
      await loadSchedules();
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
    } finally {
      setLoading(false);
      setDraggedSchedule(null);
    }
  };

  // Obtenir la couleur selon le type d'entrée
  const getEntryColor = (entryType) => {
    switch (entryType) {
      case 'Travail':
        return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700';
      case 'Télétravail':
        return 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-700';
      case 'Événement':
        return 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-400 dark:border-amber-600';
      case 'Formation':
        return 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700';
      case 'Tâche personnel':
        return 'bg-elevated-1 text-text-primary border-border-strong';
      case 'Dégustation':
        return 'bg-fuchsia-200 dark:bg-fuchsia-800/40 text-fuchsia-900 dark:text-fuchsia-100 border-fuchsia-500 dark:border-fuchsia-500';
      case 'Récupération':
      case 'recuperation':
        return 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 border-teal-300 dark:border-teal-700';
      case 'Repos':
        return 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-700';
      case 'Congé':
      case 'Congés':
        return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 border-yellow-300';
      default:
        return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700';
    }
  };

  // Obtenir l'icône selon le type d'entrée
  const getEntryIcon = (entryType) => {
    switch (entryType) {
      case 'Travail':
        return '💼';
      case 'Télétravail':
        return '🏠';
      case 'Événement':
        return '📅';
      case 'Formation':
        return '🎓';
      case 'Tâche personnel':
        return '📝';
      case 'Dégustation':
        return '🍽️';
      case 'Récupération':
      case 'recuperation':
        return '🔄';
      case 'Repos':
        return '🏖️';
      case 'Congé':
      case 'Congés':
        return '🌴';
      default:
        return '⏰';
    }
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const dayNamesShort = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  // Naviguer à la semaine contenant une date spécifique
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-6">
        <div className="flex flex-col gap-3">
          {/* Ligne 1 : Titre + switch de vue */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-text-primary">Planning</h1>
                <p className="text-text-muted text-xs hidden sm:block">Gestion du temps de travail et rendez-vous</p>
              </div>
            </div>
            {/* Switch de vue — icônes seules sur mobile */}
            <div className="flex bg-elevated-1 rounded-lg p-0.5 sm:p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2 sm:px-3 sm:py-1.5 rounded-md flex items-center space-x-1.5 transition ${
                  viewMode === 'calendar'
                    ? 'bg-surface text-blue-600 dark:text-blue-400 shadow'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
                title="Calendrier"
              >
                <Grid className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Calendrier</span>
              </button>
              <button
                onClick={() => setViewMode('weekly')}
                className={`p-2 sm:px-3 sm:py-1.5 rounded-md flex items-center space-x-1.5 transition ${
                  viewMode === 'weekly'
                    ? 'bg-surface text-blue-600 dark:text-blue-400 shadow'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
                title="Vue hebdomadaire"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Hebdo</span>
              </button>
              {authUser && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name) && (
                <button
                  onClick={() => setPersonalViewOnly(v => !v)}
                  className={`p-2 sm:px-3 sm:py-1.5 rounded-md flex items-center space-x-1.5 transition ${
                    personalViewOnly
                      ? 'bg-indigo-500 text-white shadow'
                      : 'text-text-muted hover:text-text-secondary'
                  }`}
                  title="Mon planning"
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">Moi</span>
                </button>
              )}
            </div>
          </div>
          {/* Ligne 2 : Boutons d'action — pleine largeur mobile */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                const dateToUse = selectedDate || new Date();
                setShowAddModal(true);
                setFormData(prev => ({ ...prev, date: formatDateLocal(dateToUse) }));
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-6 py-2.5 rounded-lg flex items-center justify-center space-x-1.5 sm:space-x-2 transition text-sm font-medium"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Ajouter</span>
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openCongesModal'))}
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-3 sm:px-6 py-2.5 rounded-lg flex items-center justify-center space-x-1.5 sm:space-x-2 transition shadow-md font-medium text-sm"
            >
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Congé</span>
            </button>
          </div>
        </div>
      </div>

      {/* Vue hebdomadaire (tous les rôles) */}
      {viewMode === 'weekly' && (
        <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-6">
          {/* Navigation semaine */}
          <div className="flex items-center justify-between mb-3 sm:mb-6">
            <button
              onClick={previousWeek}
              className="p-2 hover:bg-elevated-1 rounded-lg transition min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center">
              <h2 className="text-xs sm:text-xl font-bold text-center">
                <span className="hidden sm:inline">Semaine du </span>
                {getCurrentWeek()[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — {getCurrentWeek()[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </h2>
              <button
                onClick={goToToday}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-200 font-medium mt-0.5 transition"
              >
                Aujourd'hui
              </button>
            </div>
            <button
              onClick={nextWeek}
              className="p-2 hover:bg-elevated-1 rounded-lg transition min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Filtres vue hebdomadaire (Patron/Super Manager) */}
          {authUser && ['Patron', 'Super Manager'].includes(authUser.role_name) && (
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Rechercher un collaborateur..."
                  value={searchFilterPlanning}
                  onChange={e => setSearchFilterPlanning(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select value={serviceFilterPlanning} onChange={e => setServiceFilterPlanning(e.target.value)}
                className="px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Tous les services</option>
                {[...new Set(employees.filter(e => e.service_name).map(e => e.service_name))].sort().map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                onClick={() => setShowExtrasPlanning(!showExtrasPlanning)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                  showExtrasPlanning
                    ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700'
                    : 'bg-elevated-1 text-text-secondary border-border-strong hover:bg-elevated-1'
                }`}
              >
                <Users className="w-4 h-4" />
                Extras {showExtrasPlanning ? 'inclus' : 'exclus'}
              </button>
            </div>
          )}

          {/* Vue hebdo mobile : cards par jour (< 768px) */}
          <div className="md:hidden space-y-3">
            {getCurrentWeek().map((date, dayIndex) => {
              const dateStr = formatDateLocal(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const dayLabel = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });

              // Collecter toutes les entrées de tous les employés visibles pour ce jour
              const dayEntries = [];
              getSortedEmployeesForWeeklyView().forEach(employee => {
                const empSchedules = schedules.filter(s => s.employee_id === employee.id && s.date === dateStr);
                const workedHours = getWorkedHoursForEmployee(employee.id, date);
                if (empSchedules.length > 0 || workedHours !== null) {
                  dayEntries.push({ employee, schedules: empSchedules, workedHours });
                }
              });

              return (
                <div key={dayIndex} className={`rounded-lg border ${isToday ? 'border-blue-400 dark:border-blue-600 bg-blue-50/30' : 'border-border-subtle'}`}>
                  {/* En-tête du jour */}
                  <div className={`flex items-center justify-between px-3 py-2.5 ${isToday ? 'bg-blue-100/60' : 'bg-elevated-1'} rounded-t-lg`}>
                    <span className={`text-sm font-bold capitalize ${isToday ? 'text-blue-700 dark:text-blue-300' : 'text-text-secondary'}`}>
                      {dayLabel}
                    </span>
                    <button
                      onClick={() => {
                        setFormData(prev => ({ ...prev, date: dateStr }));
                        setEditingSchedule(null);
                        setShowAddModal(true);
                      }}
                      className="p-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
                      title="Ajouter une entrée"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Entrées du jour */}
                  {dayEntries.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {dayEntries.map(({ employee, schedules: empSchedules, workedHours }) => (
                        <div key={employee.id} className="px-3 py-2 flex items-start gap-2.5">
                          {/* Avatar */}
                          <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold mt-0.5 ${
                            { 'Patron': 'bg-red-500', 'Super Manager': 'bg-purple-500', 'Manager': 'bg-blue-500', 'Employé': 'bg-emerald-500', 'Extra': 'bg-orange-500' }[employee.role_name] || 'bg-gray-400'
                          }`}>
                            {(employee.first_name?.[0] || '')}{(employee.last_name?.[0] || '')}
                          </div>
                          {/* Détails */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-text-primary truncate">
                                {employee.first_name} {employee.last_name}
                              </span>
                              {workedHours !== null && (
                                <span className="text-[10px] bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                                  ✓ {workedHours.toFixed(1)}h
                                </span>
                              )}
                            </div>
                            {empSchedules.length > 0 ? (
                              <div className="mt-1 space-y-1">
                                {empSchedules.map(schedule => {
                                  const entryType = schedule.entry_type || schedule.shift_type || 'Travail';
                                  const isEventSync = (schedule.title || '').match(/^\[EVT-\d+\]/);
                                  const displayTitle = isEventSync
                                    ? (schedule.title || '').replace(/^\[EVT-\d+\]\s*/, '')
                                    : (schedule.title || entryType);
                                  return (
                                    <div
                                      key={schedule.id}
                                      onClick={(e) => { e.stopPropagation(); if (!isEventSync) handleEditSchedule(schedule); }}
                                      className={`text-[11px] px-2 py-1 rounded border-l-2 ${getEntryColor(entryType)} ${!isEventSync ? 'active:bg-elevated-1' : ''}`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium truncate">{displayTitle}</span>
                                        {isEventSync && <span className="text-[8px] bg-amber-500 text-white px-1 rounded font-bold flex-shrink-0 ml-1">EVT</span>}
                                      </div>
                                      <span className="text-[10px] opacity-70">{schedule.start_time} – {schedule.end_time}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-[10px] text-text-muted mt-0.5">Pas de planning</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-3 py-4 text-center text-xs text-text-muted">
                      Aucune entrée
                    </div>
                  )}
                </div>
              );
            })}

            {/* Résumé hebdo mobile */}
            {getSortedEmployeesForWeeklyView().length > 0 && (
              <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-3">
                <p className="text-xs font-bold text-text-secondary mb-2">Bilan de la semaine</p>
                <div className="space-y-2">
                  {getSortedEmployeesForWeeklyView().map(employee => {
                    const weekSummary = getWeekSummaryForEmployee(employee.id);
                    const contractH = parseFloat(employee.heures_hebdomadaires) || 35;
                    const remaining = contractH - weekSummary.totalPointedHours;
                    const pct = Math.min((weekSummary.totalPointedHours / contractH) * 100, 100);
                    const isOver = remaining <= 0;
                    const isComplete = remaining <= 0 && remaining > -contractH * 0.1;
                    const isMe = employee.id === (authUser?.employee_id || authUser?.id);
                    return (
                      <div key={employee.id} className={`flex items-center gap-2 ${isMe ? 'bg-blue-50/60 rounded-lg p-1.5 -mx-1.5' : ''}`}>
                        <span className="text-xs font-medium text-text-secondary w-20 truncate">
                          {employee.first_name} {employee.last_name?.[0]}.
                        </span>
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div className={`h-2 rounded-full ${isOver ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <span className={`text-[10px] font-semibold w-16 text-right ${
                          isComplete ? 'text-green-600 dark:text-green-400' : isOver ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
                        }`}>
                          {isComplete ? 'Atteint' : isOver ? `+${Math.abs(remaining).toFixed(1)}h` : `${remaining.toFixed(1)}h rest.`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Tableau hebdomadaire desktop (>= 768px) */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: '800px' }}>
              <thead>
                <tr>
                  <th className="p-2 sm:p-3 text-left font-semibold sticky left-0 bg-surface z-10 text-xs sm:text-sm border-b-2 border-border-subtle" style={{ minWidth: '160px' }}>
                    <span className="text-text-muted uppercase tracking-wider text-[10px] sm:text-xs">Collaborateurs</span>
                  </th>
                  {getCurrentWeek().map((date, index) => {
                    const isToday = date.toDateString() === new Date().toDateString();
                    return (
                      <th
                        key={index}
                        className={`p-2 sm:p-3 text-center border-b-2 ${
                          isToday ? 'border-blue-500 dark:border-blue-500' : 'border-border-subtle'
                        }`}
                        style={{ minWidth: '100px' }}
                      >
                        <div className={`text-[10px] sm:text-xs font-medium uppercase tracking-wide ${isToday ? 'text-blue-500' : 'text-text-muted'}`}>
                          {date.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '')}
                        </div>
                        <div className={`text-lg sm:text-2xl font-bold mt-0.5 ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-text-secondary'}`}>
                          {date.getDate()}
                        </div>
                        {isToday && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mx-auto mt-1" />}
                      </th>
                    );
                  })}
                  <th className="p-2 sm:p-3 text-center font-semibold border-b-2 border-border-subtle text-xs sm:text-sm" style={{ minWidth: '110px' }}>
                    <span className="text-text-muted uppercase tracking-wider text-[10px] sm:text-xs">Bilan</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getSortedEmployeesForWeeklyView().map((employee, empIndex) => {
                  const weekSchedules = getWeekSchedulesForEmployee(employee.id);
                  const weekSummary = getWeekSummaryForEmployee(employee.id);
                  const contractHours = parseFloat(employee.heures_hebdomadaires) || 35;
                  const isMe = employee.id === (authUser?.employee_id || authUser?.id);
                  const initials = `${(employee.first_name || '')[0] || ''}${(employee.last_name || '')[0] || ''}`.toUpperCase();
                  const roleColors = {
                    'Patron': 'bg-red-500', 'Super Manager': 'bg-purple-500', 'Manager': 'bg-blue-500',
                    'Employé': 'bg-emerald-500', 'Extra': 'bg-orange-500'
                  };

                  return (
                    <tr key={employee.id} className={`group transition-colors ${isMe ? 'bg-blue-50/40' : 'hover:bg-elevated-1/60'}`} style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <td className={`p-2 sm:p-3 sticky left-0 z-10 ${isMe ? 'bg-blue-50/40' : 'bg-surface group-hover:bg-elevated-1/60'}`} style={{ minWidth: '160px' }}>
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-full ${roleColors[employee.role_name] || 'bg-gray-400'} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <div className={`font-semibold text-xs sm:text-sm truncate ${isMe ? 'text-blue-700 dark:text-blue-300' : 'text-text-primary'}`}>
                              {employee.first_name} {employee.last_name}
                              {isMe && <span className="ml-1 text-[9px] text-blue-500 font-normal">(vous)</span>}
                            </div>
                            <div className="text-[10px] text-text-muted flex items-center gap-1">
                              <span>{contractHours}h/sem</span>
                              {employee.service_name && <><span className="text-gray-300">·</span><span className="truncate">{employee.service_name}</span></>}
                            </div>
                          </div>
                        </div>
                      </td>
                      {getCurrentWeek().map((date, index) => {
                        const dateStr = formatDateLocal(date);
                        const daySchedules = weekSchedules[dateStr] || [];
                        const workedHours = getWorkedHoursForEmployee(employee.id, date);
                        const isToday = date.toDateString() === new Date().toDateString();

                        return (
                          <td
                            key={index}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, date)}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                date: dateStr,
                                employee_id: String(employee.id)
                              }));
                              setEditingSchedule(null);
                              setShowAddModal(true);
                            }}
                            className={`p-1.5 sm:p-2 cursor-pointer transition-colors align-top ${
                              isToday ? 'bg-blue-50/30' : 'hover:bg-elevated-1/60'
                            }`}
                            style={{ minWidth: '100px' }}
                          >
                            {daySchedules.length > 0 ? (
                              <div className="space-y-1">
                                {daySchedules.map(schedule => {
                                  const entryType = schedule.entry_type || schedule.shift_type || 'Travail';
                                  const isEventSync = (schedule.title || '').match(/^\[EVT-\d+\]/);
                                  const displayTitle = isEventSync
                                    ? (schedule.title || '').replace(/^\[EVT-\d+\]\s*/, '')
                                    : (schedule.title || entryType);
                                  const estimatedHours = calculateEstimatedHours(
                                    schedule.start_time,
                                    schedule.end_time,
                                    entryType
                                  );

                                  // Détecter les conflits (même employé, même jour, créneaux qui se chevauchent)
                                  // Les Tâches personnelles n'émettent ni ne déclenchent jamais d'alerte
                                  const hasConflict = entryType !== 'Tâche personnel' && daySchedules.some(other =>
                                    other.id !== schedule.id &&
                                    (other.entry_type || other.shift_type || 'Travail') !== 'Tâche personnel' &&
                                    other.start_time < schedule.end_time &&
                                    other.end_time > schedule.start_time
                                  );

                                  return (
                                    <div
                                      key={schedule._isCuisinier ? `${schedule.id}_c` : schedule.id}
                                      draggable={!schedule._isCuisinier && !isEventSync}
                                      onDragStart={!schedule._isCuisinier && !isEventSync ? (e) => handleDragStart(e, schedule) : undefined}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`text-xs p-1.5 sm:p-2 rounded-lg border-l-[3px] ${getEntryColor(entryType)} ${hasConflict ? 'ring-2 ring-red-400' : ''} hover:shadow-md transition-all ${schedule._isCuisinier ? 'opacity-80' : isEventSync ? 'cursor-default' : 'cursor-move'} group/entry relative`}
                                      title={schedule._isCuisinier ? `👨‍🍳 Cuisinier — ${schedule.start_time}-${schedule.end_time}` : isEventSync ? `Événement (géré automatiquement) — ${schedule.description || ''}` : displayTitle}
                                    >
                                      {isEventSync && (
                                        <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[8px] px-1 py-0.5 rounded-full font-bold leading-none">
                                          EVT
                                        </span>
                                      )}
                                      {hasConflict && (
                                        <span className="absolute -top-1.5 -left-1.5 text-red-500" title="Conflit de planning">
                                          <AlertTriangle className="w-3 h-3" />
                                        </span>
                                      )}
                                      <div className="font-semibold truncate text-[11px]">
                                        {schedule._isCuisinier ? '👨‍🍳 Cuisinier' : displayTitle}
                                      </div>
                                      <div className="text-[10px] opacity-70 mt-0.5">
                                        {schedule.start_time} – {schedule.end_time}
                                      </div>
                                      {schedule.location && (
                                        <div className="text-[9px] opacity-60 mt-0.5 flex items-center gap-0.5">
                                          <MapPin className="w-2.5 h-2.5" />{schedule.location}
                                        </div>
                                      )}
                                      {['Travail', 'Événement', 'Formation'].includes(entryType) && (
                                        <div className="text-[9px] opacity-60 mt-0.5">
                                          {estimatedHours.toFixed(1)}h estimé
                                        </div>
                                      )}

                                      {/* Boutons d'actions (apparaissent au hover) — masqués pour les événements sync */}
                                      {!schedule._isCuisinier && !isEventSync && <div className="absolute top-0.5 right-0.5 hidden group-hover/entry:flex items-center gap-0.5 bg-white/95 backdrop-blur-sm rounded-md shadow-lg p-0.5">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditSchedule(schedule);
                                          }}
                                          className="text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 p-1 rounded transition"
                                          title="Modifier"
                                        >
                                          <Edit className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDuplicateSchedule(schedule);
                                          }}
                                          className="text-green-600 hover:bg-green-50 dark:bg-green-950/40 p-1 rounded transition"
                                          title="Dupliquer"
                                        >
                                          <Copy className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteSchedule(schedule.id);
                                          }}
                                          className="text-red-600 hover:bg-red-50 dark:bg-red-950/40 p-1 rounded transition"
                                          title="Supprimer"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>}
                                    </div>
                                  );
                                })}
                                {workedHours !== null && (
                                  <div className="text-[10px] text-center mt-1 px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-full font-medium">
                                    ✓ {workedHours.toFixed(1)}h pointé
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="h-8 flex items-center justify-center">
                                <div className="w-5 h-[1px] bg-gray-200" />
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="p-2 sm:p-3" style={{ minWidth: '110px' }}>
                        <div className="space-y-2">
                          <div className="bg-emerald-50 dark:bg-emerald-950/40 rounded-lg px-2.5 py-1.5 text-center">
                            <div className="font-bold text-base text-emerald-700">
                              {weekSummary.totalPointedHours.toFixed(1)}h
                            </div>
                            <div className="text-[9px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                              pointé
                            </div>
                          </div>
                          <div className="bg-elevated-1 rounded-lg px-2.5 py-1.5 text-center">
                            <div className="font-semibold text-sm text-text-secondary">
                              {weekSummary.totalPlannedHours.toFixed(1)}h
                            </div>
                            <div className="text-[9px] text-text-muted uppercase tracking-wide">
                              planifié
                            </div>
                          </div>
                          {contractHours > 0 && (() => {
                            const pct = Math.min((weekSummary.totalPointedHours / contractHours) * 100, 100);
                            const remaining = contractHours - weekSummary.totalPointedHours;
                            const isOver = remaining <= 0;
                            const isComplete = remaining <= 0 && remaining > -contractHours * 0.1;
                            return (
                              <div className="space-y-1.5">
                                {/* Barre de progression */}
                                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div
                                    className={`h-2 rounded-full transition-all ${isOver ? 'bg-green-500' : 'bg-blue-500'}`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  />
                                </div>
                                {/* Ratio pointé / contrat */}
                                <div className="text-[10px] text-center text-text-muted">
                                  {weekSummary.totalPointedHours.toFixed(1)}h / {contractHours}h
                                </div>
                                {/* Heures restantes ou supplémentaires */}
                                <div className={`text-[10px] font-semibold text-center ${
                                  isComplete ? 'text-green-600 dark:text-green-400' :
                                  isOver ? 'text-orange-600 dark:text-orange-400' :
                                  'text-blue-600 dark:text-blue-400'
                                }`}>
                                  {isComplete
                                    ? 'Contrat atteint'
                                    : isOver
                                      ? `+${Math.abs(remaining).toFixed(1)}h sup.`
                                      : `${remaining.toFixed(1)}h restantes`
                                  }
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Section Temps de travail - Estimé vs Réel (pliable sur mobile) */}
          {visibleEmployees.length > 0 && (
            <div className="mt-4 sm:mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 sm:p-6">
              <button
                onClick={() => setShowStatsSection(v => !v)}
                className="w-full flex items-center justify-between sm:cursor-default"
              >
                <h3 className="text-base sm:text-xl font-bold flex items-center">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-blue-600 dark:text-blue-400" />
                  Temps de travail
                </h3>
                <ChevronRight className={`w-5 h-5 text-text-muted sm:hidden transition-transform ${showStatsSection ? 'rotate-90' : ''}`} />
              </button>
              <div className={`${showStatsSection ? 'block' : 'hidden'} sm:block mt-3 sm:mt-4`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {visibleEmployees.map(employee => {
                  const stats = getEmployeeStats(employee.id);
                  return (
                    <div key={employee.id} className="bg-surface rounded-lg p-4 shadow-sm">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                          {employee.avatar || 'NA'}
                        </div>
                        <div>
                          <p className="font-semibold text-text-primary">{employee.first_name} {employee.last_name}</p>
                          <p className="text-xs text-text-muted">{employee.role_name}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {/* Semaine */}
                        <div>
                          <p className="text-xs font-medium text-text-secondary mb-1">Cette semaine</p>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-xs text-text-muted">Estimé</p>
                              <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{stats.weekEstimatedHours}h</p>
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-text-muted">Réel</p>
                              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">{stats.weekRealHours}h</p>
                            </div>
                          </div>
                          {/* Heures restantes cette semaine */}
                          {(() => {
                            const weekContractH = parseFloat(employee.heures_hebdomadaires) || 35;
                            const weekReal = parseFloat(stats.weekRealHours);
                            const pct = Math.min((weekReal / weekContractH) * 100, 100);
                            const remaining = weekContractH - weekReal;
                            const isOver = remaining <= 0;
                            const isComplete = remaining <= 0 && remaining > -weekContractH * 0.1;
                            return (
                              <div className="mt-2 space-y-1">
                                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className={`h-1.5 rounded-full transition-all ${isOver ? 'bg-green-500' : 'bg-blue-500'}`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  />
                                </div>
                                <div className="flex justify-between items-center text-[10px]">
                                  <span className="text-text-muted">{weekReal.toFixed(1)} / {weekContractH}h</span>
                                  <span className={`font-semibold ${
                                    isComplete ? 'text-green-600 dark:text-green-400' : isOver ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
                                  }`}>
                                    {isComplete ? 'Atteint' : isOver ? `+${Math.abs(remaining).toFixed(1)}h sup.` : `${remaining.toFixed(1)}h rest.`}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        {/* Mois */}
                        <div>
                          <p className="text-xs font-medium text-text-secondary mb-1">Ce mois</p>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-xs text-text-muted">Estimé</p>
                              <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">{stats.monthEstimatedHours}h</p>
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-text-muted">Réel</p>
                              <p className="text-sm font-semibold text-purple-800 dark:text-purple-200">{stats.monthRealHours}h</p>
                            </div>
                          </div>
                        </div>
                        {/* Modulation mensuelle */}
                        <div className={`p-3 rounded-lg border ${getModulationBgColor(stats.modulationMensuelle)}`}>
                          <p className="text-xs font-medium text-text-secondary mb-1 flex items-center gap-1">
                            {stats.modulationMensuelle !== null && stats.modulationMensuelle >= 0 
                              ? <TrendingUp className="w-3 h-3" /> 
                              : <TrendingDown className="w-3 h-3" />}
                            Modulation du mois
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-xs text-text-muted">Contrat</p>
                              <p className="text-sm font-semibold text-text-secondary">
                                {stats.heuresContratMensuel ? `${stats.heuresContratMensuel.toFixed(1)}h/mois` : '—'}
                              </p>
                            </div>
                            <div className="flex-1 text-right">
                              <p className="text-xs text-text-muted">Solde</p>
                              <p className={`text-lg font-bold ${getModulationColor(stats.modulationMensuelle)}`}>
                                {formatHeuresModulation(stats.modulationMensuelle)}
                              </p>
                            </div>
                          </div>
                          {/* Alerte mensuelle */}
                          {stats.alerteMensuelle && stats.alerteMensuelle.level !== 'ok' && (
                            <div className={`mt-2 flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md ${
                              stats.alerteMensuelle.level === 'danger' 
                                ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' 
                                : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                            }`}>
                              <AlertTriangle className="w-3 h-3" />
                              <span>{stats.alerteMensuelle.icon} {stats.alerteMensuelle.message}</span>
                            </div>
                          )}
                          {stats.alerteMensuelle && stats.alerteMensuelle.level === 'ok' && (
                            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                              <CheckCircle className="w-3 h-3" />
                              <span>{stats.alerteMensuelle.icon} {stats.alerteMensuelle.message}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vue calendrier (mode par défaut) */}
      {viewMode === 'calendar' && (
        <>
      {/* Détails jour sélectionné - Agenda du jour */}
      {selectedDate && (
        <div className="bg-surface rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">
            Agenda du {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </h3>

          {/* Plannings */}
          {getSchedulesForDay(selectedDate).length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-text-secondary mb-3">Entrées Planning</h4>
              <div className="space-y-3">
                {getSchedulesForDay(selectedDate).map(schedule => {
                const displayEmpId = schedule._isCuisinier ? parseInt(schedule.degustation_cuisinier_id) : schedule.employee_id;
                const employee = employees.find(e => e.id === displayEmpId);
                const workedHours = getWorkedHoursForEmployee(schedule.employee_id, selectedDate);
                const entryType = schedule.entry_type || schedule.shift_type || 'Travail';
                const estimatedHours = calculateEstimatedHours(schedule.start_time, schedule.end_time, entryType);
                const isEventSync = (schedule.title || '').match(/^\[EVT-\d+\]/);
                const displayTitle = isEventSync
                  ? (schedule.title || '').replace(/^\[EVT-\d+\]\s*/, '')
                  : schedule.title;

                // Détecter les conflits (même employé, même jour, créneaux qui se chevauchent)
                // Les Tâches personnelles n'émettent ni ne déclenchent jamais d'alerte
                const daySchedulesForEmp = getSchedulesForDay(selectedDate).filter(s => s.employee_id === schedule.employee_id);
                const hasConflict = entryType !== 'Tâche personnel' && daySchedulesForEmp.some(other =>
                  other.id !== schedule.id &&
                  (other.entry_type || other.shift_type || 'Travail') !== 'Tâche personnel' &&
                  other.start_time < schedule.end_time &&
                  other.end_time > schedule.start_time
                );

                return (
                  <div key={schedule._isCuisinier ? `${schedule.id}_c` : schedule.id} className={`flex items-center justify-between p-4 border-l-4 rounded-lg hover:bg-elevated-1 ${getEntryColor(entryType).replace('text-', 'border-').split(' ')[0]} ${hasConflict ? 'ring-2 ring-red-400 bg-red-50/30' : ''}`}>
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="text-3xl">
                        {getEntryIcon(entryType)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1 flex-wrap">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getEntryColor(entryType)}`}>
                            {entryType}
                          </span>
                          {isEventSync && (
                            <span className="px-2 py-1 rounded text-xs font-bold bg-amber-500 text-white">EVT auto</span>
                          )}
                          {hasConflict && (
                            <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Conflit
                            </span>
                          )}
                          {schedule._isCuisinier && (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-800 dark:text-fuchsia-200">Rôle : Cuisinier</span>
                          )}
                          {displayTitle && (
                            <span className="font-semibold text-text-primary">{displayTitle}</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                            {employee?.avatar || 'NA'}
                          </div>
                          <p className="font-semibold text-text-secondary">{employee?.first_name} {employee?.last_name}</p>
                        </div>
                        {schedule.description && (
                          <p className="text-sm text-text-secondary mt-2">{schedule.description}</p>
                        )}
                        {isEventSync && schedule.location && (
                          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1 flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> {schedule.location}
                          </p>
                        )}
                        {entryType === 'Dégustation' && (
                          <div className="mt-2 p-2 bg-fuchsia-50 dark:bg-fuchsia-950/40 border border-fuchsia-200 dark:border-fuchsia-800 rounded-lg space-y-1">
                            {schedule.degustation_cuisinier_id && (() => {
                              const cuisinier = employees.find(e => e.id === parseInt(schedule.degustation_cuisinier_id));
                              return cuisinier ? (
                                <p className="text-xs font-semibold text-fuchsia-800 dark:text-fuchsia-200">
                                  Cuisinier : {cuisinier.first_name} {cuisinier.last_name}
                                </p>
                              ) : null;
                            })()}
                            {schedule.degustation_menu && (
                              <div>
                                <p className="text-xs font-semibold text-fuchsia-700">Menu :</p>
                                <p className="text-xs text-fuchsia-600 dark:text-fuchsia-400 whitespace-pre-wrap">{schedule.degustation_menu}</p>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center space-x-3 mt-2 text-sm text-text-secondary">
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {schedule.start_time} - {schedule.end_time}
                          </span>
                          {['Travail', 'Événement', 'Formation'].includes(entryType) && (
                            <span className="flex items-center font-semibold text-blue-600 dark:text-blue-400">
                              <Briefcase className="w-4 h-4 mr-1" />
                              {estimatedHours.toFixed(2)}h estimées
                            </span>
                          )}
                          {schedule.location && !isEventSync && (
                            <span className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {schedule.location}
                            </span>
                          )}
                          {workedHours !== null && (
                            <span className="px-2 py-1 rounded text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 font-semibold">
                              {workedHours}h travaillées
                            </span>
                          )}
                        </div>
                        {isEventSync && (
                          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 italic">Planifié automatiquement depuis le module Événements</p>
                        )}
                      </div>
                    </div>
                    {!schedule._isCuisinier && !isEventSync && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditSchedule(schedule)}
                        className="text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 p-2 rounded transition"
                        title="Modifier"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicateSchedule(schedule)}
                        className="text-green-600 hover:bg-green-50 dark:bg-green-950/40 p-2 rounded transition"
                        title="Dupliquer"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        className="text-red-600 hover:bg-red-50 dark:bg-red-950/40 p-2 rounded transition"
                        title="Supprimer"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    )}
                  </div>
                );
                })}
              </div>
            </div>
          )}

          {/* Tâches */}
          {getTasksForDay(selectedDate).length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-text-secondary mb-3">Tâches à réaliser</h4>
              <div className="space-y-3">
                {getTasksForDay(selectedDate).map(task => {
                  const assignedEmployee = employees.find(e => e.id === task.assigned_to);
                  return (
                    <div key={task.id} className="flex items-center justify-between p-4 border-l-4 border-pink-500 bg-pink-50 dark:bg-pink-950/40 rounded-lg hover:shadow-md transition">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center text-white font-bold text-xl">
                          📋
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-text-primary">{task.title}</p>
                          {task.description && (
                            <p className="text-sm text-text-secondary mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center space-x-3 mt-2 text-sm text-text-secondary">
                            {assignedEmployee && (
                              <span className="flex items-center">
                                <Users className="w-4 h-4 mr-1" />
                                {assignedEmployee.first_name} {assignedEmployee.last_name}
                              </span>
                            )}
                            {task.scheduled_start_time && task.scheduled_end_time && (
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {task.scheduled_start_time} - {task.scheduled_end_time}
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              task.priority === 'Haute' ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200' :
                              task.priority === 'Moyenne' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200' :
                              'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                            }`}>
                              {task.priority}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              task.status === 'Terminé' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200' :
                              task.status === 'En cours' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200' :
                              'bg-elevated-1 text-text-primary'
                            }`}>
                              {task.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Message si aucun planning ni tâche */}
          {getSchedulesForDay(selectedDate).length === 0 && getTasksForDay(selectedDate).length === 0 && (
            <p className="text-center text-text-muted py-8">Aucune entrée dans l'agenda ce jour</p>
          )}
        </div>
      )}

      {/* Calendrier */}
      <div className="bg-surface rounded-xl shadow-lg p-6">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <button onClick={previousMonth} className="p-2 hover:bg-elevated-1 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div className="flex flex-col items-center">
            <h2 className="text-lg sm:text-2xl font-bold">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              onClick={goToToday}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-200 font-medium mt-0.5 transition"
            >
              Aujourd'hui
            </button>
          </div>
          <button onClick={nextMonth} className="p-2 hover:bg-elevated-1 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Jours semaine — 1 lettre mobile, 3 lettres desktop */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
          {dayNames.map((day, i) => (
            <div key={day} className="text-center font-semibold text-text-muted py-1 sm:py-2 text-xs sm:text-sm">
              <span className="sm:hidden">{dayNamesShort[i]}</span>
              <span className="hidden sm:inline">{day}</span>
            </div>
          ))}
        </div>

        {/* Grille calendrier */}
        <div key={`calendar-${refreshKey}`} className="grid grid-cols-7 gap-1 sm:gap-2">
          {getDaysInMonth().map((date, index) => {
            const daySchedules = date ? getSchedulesForDay(date) : [];
            const dayTasks = date ? getTasksForDay(date) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();
            const isSelected = selectedDate && date && date.toDateString() === selectedDate.toDateString();

            return (
              <div
                key={index}
                onClick={() => date && setSelectedDate(date)}
                onDragOver={date ? handleDragOver : undefined}
                onDrop={date ? (e) => handleDrop(e, date) : undefined}
                className={`min-h-[52px] sm:min-h-24 p-1 sm:p-2 border rounded-lg cursor-pointer transition ${
                  !date ? 'bg-elevated-1 cursor-default' :
                  isToday ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40' :
                  isSelected ? 'border-blue-400 bg-blue-100 dark:bg-blue-900/40' :
                  'border-border-subtle hover:bg-elevated-1'
                }`}
              >
                {date && (
                  <>
                    <div className={`text-xs sm:text-sm font-semibold mb-0.5 sm:mb-1 ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-text-secondary'}`}>
                      {date.getDate()}
                    </div>

                    {/* Mobile : indicateurs compacts (points colorés + compteur) */}
                    <div className="sm:hidden">
                      {daySchedules.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mb-0.5">
                          {daySchedules.slice(0, 4).map((schedule, si) => {
                            const entryType = schedule.entry_type || schedule.shift_type || 'Travail';
                            const colorMap = {
                              'Travail': 'bg-blue-500', 'Télétravail': 'bg-cyan-500', 'Événement': 'bg-amber-500',
                              'Formation': 'bg-purple-500', 'Dégustation': 'bg-fuchsia-500', 'Repos': 'bg-orange-400',
                              'Congé': 'bg-yellow-500', 'Congés': 'bg-yellow-500'
                            };
                            return <div key={si} className={`w-2 h-2 rounded-full ${colorMap[entryType] || 'bg-gray-400'}`} />;
                          })}
                        </div>
                      )}
                      {daySchedules.length > 0 && (
                        <div className="text-[9px] text-text-muted leading-tight">
                          {daySchedules[0].start_time}
                        </div>
                      )}
                      {getWorkedHoursForEmployee(authUser?.employee_id || authUser?.id, date) !== null && (
                        <div className="text-[9px] text-emerald-600 dark:text-emerald-400 font-semibold">
                          ✓ {getWorkedHoursForEmployee(authUser?.employee_id || authUser?.id, date).toFixed(1)}h
                        </div>
                      )}
                      {dayTasks.length > 0 && (
                        <div className="w-2 h-2 rounded-full bg-pink-500 mt-0.5" />
                      )}
                      {(daySchedules.length + dayTasks.length) > 4 && (
                        <div className="text-[8px] text-text-muted font-medium">
                          +{daySchedules.length + dayTasks.length - 4}
                        </div>
                      )}
                    </div>

                    {/* Desktop : détails complets */}
                    <div className="hidden sm:block space-y-1">
                      {daySchedules.slice(0, 3).map(schedule => {
                        const displayEmpId = schedule._isCuisinier ? parseInt(schedule.degustation_cuisinier_id) : schedule.employee_id;
                        const employee = employees.find(e => e.id === displayEmpId);
                        const workedHours = getWorkedHoursForEmployee(schedule.employee_id, date);
                        const entryType = schedule.entry_type || schedule.shift_type || 'Travail';
                        const isEventSync = (schedule.title || '').match(/^\[EVT-\d+\]/);

                        return (
                          <div
                            key={schedule._isCuisinier ? `${schedule.id}_c` : schedule.id}
                            draggable={!schedule._isCuisinier && !isEventSync}
                            onDragStart={!schedule._isCuisinier && !isEventSync ? (e) => handleDragStart(e, schedule) : undefined}
                            className={`text-xs px-2 py-1 rounded border-l-2 ${isEventSync ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/40' : getEntryColor(entryType).split(' ')[0].replace('text-', 'border-') + ' bg-surface'} hover:shadow-sm transition-shadow ${schedule._isCuisinier ? 'opacity-80' : isEventSync ? 'cursor-default' : 'cursor-move'}`}
                            title={schedule._isCuisinier ? `${employee?.first_name} ${employee?.last_name} — Cuisinier` : isEventSync ? `Événement auto — ${schedule.location || ''}` : `${entryType} - ${schedule.start_time} à ${schedule.end_time}${schedule.title ? ' - ' + schedule.title : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium truncate">
                                {getEntryIcon(entryType)} {employee?.first_name?.charAt(0)}.{employee?.last_name}
                              </span>
                              <span className="text-[10px] opacity-60 ml-1">
                                {schedule.start_time}
                              </span>
                            </div>
                            {workedHours !== null && (
                              <div className="text-[10px] text-green-600 dark:text-green-400 font-semibold mt-0.5">
                                {workedHours.toFixed(1)}h
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {dayTasks.slice(0, 3 - daySchedules.length).map(task => (
                        <div key={`task-${task.id}`} className="text-xs px-2 py-1 rounded border-l-2 border-pink-400 bg-pink-50 dark:bg-pink-950/40 truncate">
                          📋 {task.title.length > 15 ? task.title.substring(0, 15) + '...' : task.title}
                        </div>
                      ))}

                      {(daySchedules.length + dayTasks.length) > 3 && (
                        <div className="text-[10px] text-center text-text-muted font-medium py-0.5">
                          +{daySchedules.length + dayTasks.length - 3}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Statistiques employés (pliable sur mobile) */}
      {visibleEmployees.length > 0 && (
        <div key={refreshKey} className="bg-surface rounded-xl shadow-lg p-3 sm:p-6">
          <button
            onClick={() => setShowStatsSection(v => !v)}
            className="w-full flex items-center justify-between sm:cursor-default"
          >
            <h2 className="text-base sm:text-xl font-bold text-text-primary flex items-center">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-blue-600 dark:text-blue-400" />
              Temps de travail
            </h2>
            <ChevronRight className={`w-5 h-5 text-text-muted sm:hidden transition-transform ${showStatsSection ? 'rotate-90' : ''}`} />
          </button>
          <div className={`${showStatsSection ? 'block' : 'hidden'} sm:block mt-3 sm:mt-4`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {visibleEmployees.map(employee => {
              const stats = getEmployeeStats(employee.id);
              return (
                <div key={employee.id} className="border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">
                      {employee.avatar || 'NA'}
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary">{employee.first_name} {employee.last_name}</p>
                      <p className="text-xs text-text-muted">{employee.role_name}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {/* Semaine */}
                    <div className="bg-blue-50 dark:bg-blue-950/40 p-3 rounded-lg">
                      <p className="text-xs text-text-secondary mb-2 font-semibold">Cette semaine</p>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-text-muted">Estimé</p>
                          <p className="text-lg font-bold text-blue-400">{stats.weekEstimatedHours}h</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-text-muted">Réel</p>
                          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{stats.weekRealHours}h</p>
                        </div>
                      </div>
                      {/* Heures restantes cette semaine */}
                      {(() => {
                        const weekContractH = parseFloat(employee.heures_hebdomadaires) || 35;
                        const weekReal = parseFloat(stats.weekRealHours);
                        const pct = Math.min((weekReal / weekContractH) * 100, 100);
                        const remaining = weekContractH - weekReal;
                        const isOver = remaining <= 0;
                        const isComplete = remaining <= 0 && remaining > -weekContractH * 0.1;
                        return (
                          <div className="mt-2 pt-2 border-t border-blue-200 space-y-1.5">
                            <div className="w-full bg-blue-200 dark:bg-blue-800/40 rounded-full h-2 overflow-hidden">
                              <div
                                className={`h-2 rounded-full transition-all ${isOver ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                            <div className="flex justify-between items-center text-[10px]">
                              <span className="text-text-muted">{weekReal.toFixed(1)}h / {weekContractH}h</span>
                              <span className={`font-semibold ${
                                isComplete ? 'text-green-600 dark:text-green-400' : isOver ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
                              }`}>
                                {isComplete ? 'Atteint' : isOver ? `+${Math.abs(remaining).toFixed(1)}h sup.` : `${remaining.toFixed(1)}h rest.`}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    {/* Mois */}
                    <div className="bg-purple-50 dark:bg-purple-950/40 p-3 rounded-lg">
                      <p className="text-xs text-text-secondary mb-2 font-semibold">Ce mois</p>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-text-muted">Estimé</p>
                          <p className="text-lg font-bold text-purple-400">{stats.monthEstimatedHours}h</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-text-muted">Réel</p>
                          <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{stats.monthRealHours}h</p>
                        </div>
                      </div>
                    </div>
                    {/* Modulation mensuelle */}
                    <div className={`p-3 rounded-lg border ${getModulationBgColor(stats.modulationMensuelle)}`}>
                      <p className="text-xs text-text-secondary mb-2 font-semibold flex items-center gap-1">
                        {stats.modulationMensuelle !== null && stats.modulationMensuelle >= 0 
                          ? <TrendingUp className="w-3 h-3" /> 
                          : <TrendingDown className="w-3 h-3" />}
                        Modulation du mois
                      </p>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-text-muted">Contrat</p>
                          <p className="text-sm font-bold text-text-secondary">
                            {stats.heuresContratMensuel ? `${stats.heuresContratMensuel.toFixed(1)}h` : '—'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-text-muted">Solde</p>
                          <p className={`text-xl font-bold ${getModulationColor(stats.modulationMensuelle)}`}>
                            {formatHeuresModulation(stats.modulationMensuelle)}
                          </p>
                        </div>
                      </div>
                      {/* Alerte mensuelle */}
                      {stats.alerteMensuelle && stats.alerteMensuelle.level !== 'ok' && (
                        <div className={`mt-2 flex items-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded-md ${
                          stats.alerteMensuelle.level === 'danger' 
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' 
                            : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                        }`}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span>{stats.alerteMensuelle.icon} {stats.alerteMensuelle.message}</span>
                        </div>
                      )}
                      {stats.alerteMensuelle && stats.alerteMensuelle.level === 'ok' && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded-md bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span>{stats.alerteMensuelle.icon} {stats.alerteMensuelle.message}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Modal ajout */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={() => { setShowAddModal(false); setEditingSchedule(null); }}>
          <div className="bg-surface w-full sm:max-w-2xl sm:mx-4 rounded-t-2xl sm:rounded-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Handle mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>
            {/* Header sticky */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h2 className="text-lg font-semibold text-text-primary">
                {editingSchedule ? 'Modifier l\'entrée' : 'Ajouter au Planning'}
              </h2>
              <button onClick={() => { setShowAddModal(false); setEditingSchedule(null); }} className="p-2 hover:bg-elevated-1 rounded-full">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Corps scrollable */}
            <form onSubmit={editingSchedule ? handleUpdateSchedule : handleAddSchedule} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Employe *</label>
                <select
                  required
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                >
                  <option value="">Selectionner un employe</option>
                  {visibleEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Type d'entree *</label>
                <select
                  value={formData.entry_type}
                  onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                >
                  <option value="Travail">💼 Travail (avec pause 45min si &gt;4h)</option>
                  <option value="Télétravail">🏠 Télétravail (avec pause 45min si &gt;4h)</option>
                  <option value="Événement">📅 Événement</option>
                  <option value="Dégustation">🍽️ Dégustation</option>
                  <option value="Formation">🎓 Formation (avec pause 1h00 si &gt;4h)</option>
                  <option value="Récupération">🔄 Récupération</option>
                  <option value="Tâche personnel">📝 Tâche personnel</option>
                  <option value="Repos">🏖️ Repos</option>
                  <option value="Congé">🌴 Congé</option>
                </select>
              </div>

              {/* Sous-sélecteur Congé */}
              {formData.entry_type === 'Congé' && (
                <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 rounded-lg p-3 sm:p-4 space-y-3">
                  <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">🌴 Type de congé</p>
                  <select
                    required
                    value={formData.conge_type}
                    onChange={(e) => setFormData({ ...formData, conge_type: e.target.value, recuperation_hours: '' })}
                    className="w-full px-3 py-2.5 sm:py-2 border border-yellow-300 dark:border-yellow-700 rounded-lg min-h-[44px] text-base sm:text-sm focus:ring-2 focus:ring-yellow-400"
                  >
                    <option value="">Choisir le type de congé</option>
                    {['Patron', 'Super Manager'].includes(authUser?.role_name) && (
                      <option value="congé_payé">Congé payé (déduit du solde CP)</option>
                    )}
                    <option value="congé_sans_solde">Congé sans solde</option>
                  </select>
                  {formData.conge_type === 'congé_payé' && soldeCp !== null && (
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                      Solde CP : <strong>{soldeCp} jour(s) restant(s)</strong>
                    </p>
                  )}
                </div>
              )}

              {/* Champs spécifiques Récupération */}
              {formData.entry_type === 'Récupération' && (
                <div className="bg-teal-50 dark:bg-teal-950/40 border border-teal-200 rounded-lg p-3 sm:p-4 space-y-3">
                  <p className="text-sm font-semibold text-teal-900 dark:text-teal-100">🔄 Récupération d'heures</p>
                  {modulationSolde !== null && (
                    <p className={`text-xs ${modulationSolde > 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      Solde modulation : <strong>{modulationSolde.toFixed(1)}h</strong>
                      {modulationSolde <= 0 && ' — Insuffisant pour poser une récupération'}
                    </p>
                  )}
                  <div>
                    <label className="block text-xs font-medium mb-1 text-teal-800 dark:text-teal-200">Heures à récupérer</label>
                    <select
                      value={formData.recuperation_hours}
                      onChange={(e) => setFormData({ ...formData, recuperation_hours: e.target.value })}
                      className="w-full px-3 py-2 border border-teal-300 dark:border-teal-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-400"
                    >
                      <option value="">Calculer depuis les horaires</option>
                      <option value="3.5">3.5h (demi-journée)</option>
                      <option value="7">7h (journée complète)</option>
                      <option value="custom">Heures personnalisées</option>
                    </select>
                  </div>
                  {formData.recuperation_hours === 'custom' && (
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max={modulationSolde || 0}
                      placeholder="Nombre d'heures"
                      onChange={(e) => setFormData({ ...formData, recuperation_hours: e.target.value })}
                      className="w-full px-3 py-2 border border-teal-300 dark:border-teal-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-400"
                    />
                  )}
                </div>
              )}

              {/* Champs spécifiques Dégustation */}
              {formData.entry_type === 'Dégustation' && (
                <div className="bg-fuchsia-50 dark:bg-fuchsia-950/40 border border-fuchsia-200 rounded-lg p-3 sm:p-4 space-y-3">
                  <p className="text-sm font-semibold text-fuchsia-900">🍽️ Détails de la dégustation</p>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-fuchsia-800 dark:text-fuchsia-200">Cuisinier assigné *</label>
                    <select
                      required={formData.entry_type === 'Dégustation'}
                      value={formData.degustation_cuisinier_id}
                      onChange={(e) => setFormData({ ...formData, degustation_cuisinier_id: e.target.value })}
                      className="w-full px-3 py-2.5 sm:py-2 border border-fuchsia-300 dark:border-fuchsia-700 rounded-lg min-h-[44px] text-base sm:text-sm focus:ring-2 focus:ring-fuchsia-400"
                    >
                      <option value="">Sélectionner un cuisinier</option>
                      {visibleEmployees.filter(emp => emp.service_name === 'Cuisine').length === 0 && (
                        <option disabled value="">— Aucun employé du service Cuisine —</option>
                      )}
                      {visibleEmployees
                        .filter(emp => emp.service_name === 'Cuisine')
                        .map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name} — {emp.role_name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-fuchsia-800 dark:text-fuchsia-200">Menu *</label>
                    <textarea
                      required={formData.entry_type === 'Dégustation'}
                      value={formData.degustation_menu}
                      onChange={(e) => setFormData({ ...formData, degustation_menu: e.target.value })}
                      placeholder="Ex: Amuse-bouche, Saumon mariné, Magret de canard, Tarte tatin..."
                      rows={3}
                      className="w-full px-3 py-2.5 sm:py-2 border border-fuchsia-300 dark:border-fuchsia-700 rounded-lg min-h-[44px] text-base sm:text-sm focus:ring-2 focus:ring-fuchsia-400 resize-none"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Titre</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Réunion client, Formation React, RDV médecin..."
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Détails supplémentaires..."
                  rows={2}
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Date de début *</label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                />
              </div>

              {!editingSchedule && (
                <div>
                  <label className="block text-sm font-medium mb-1">Récurrence</label>
                  <select
                    value={formData.recurrence}
                    onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                    className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                  >
                    <option value="none">🔹 Entrée unique (pas de récurrence)</option>
                    <option value="working_week">📋 Semaine ouvrée (Lun → Ven)</option>
                    <option value="daily">📅 Quotidienne (tous les jours)</option>
                    <option value="weekly">📆 Hebdomadaire (chaque semaine)</option>
                    <option value="monthly">🗓️ Mensuelle (chaque mois)</option>
                  </select>
                </div>
              )}

              {!editingSchedule && formData.recurrence !== 'none' && (
                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <label className="block text-sm font-medium mb-1">Date de fin de récurrence</label>
                  <input
                    type="date"
                    value={formData.recurrence_end_date}
                    onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                    placeholder="Optionnel - Par défaut 3 mois"
                    className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    💡 Si vide, la récurrence durera 3 mois à partir de la date de début. Maximum 365 entrées.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Heure de début *</label>
                  <input
                    type="time"
                    required
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Heure de fin *</label>
                  <input
                    type="time"
                    required
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                  />
                </div>
              </div>

              {/* Aperçu du nombre d'entrées qui seront créées */}
              {formData.recurrence !== 'none' && formData.date && (
                <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 rounded-lg p-3">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                    🔄 {(() => {
                      const startDate = new Date(formData.date + 'T00:00:00');
                      const endDate = formData.recurrence_end_date
                        ? new Date(formData.recurrence_end_date + 'T00:00:00')
                        : new Date(startDate);
                      if (!formData.recurrence_end_date) {
                        endDate.setMonth(endDate.getMonth() + 3);
                      }

                      let count = 0;
                      let currentDate = new Date(startDate);

                      while (currentDate <= endDate && count < 365) {
                        if (formData.recurrence === 'working_week') {
                          const dow = currentDate.getDay();
                          if (dow >= 1 && dow <= 5) count++;
                          currentDate.setDate(currentDate.getDate() + 1);
                        } else {
                          count++;
                          if (formData.recurrence === 'daily') {
                            currentDate.setDate(currentDate.getDate() + 1);
                          } else if (formData.recurrence === 'weekly') {
                            currentDate.setDate(currentDate.getDate() + 7);
                          } else if (formData.recurrence === 'monthly') {
                            currentDate.setMonth(currentDate.getMonth() + 1);
                          }
                        }
                      }

                      return `${count} entrée(s) seront créées avec cette récurrence`;
                    })()}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    Du {new Date(formData.date + 'T00:00:00').toLocaleDateString('fr-FR')} au {(() => {
                      const endDate = formData.recurrence_end_date
                        ? new Date(formData.recurrence_end_date + 'T00:00:00')
                        : new Date(formData.date + 'T00:00:00');
                      if (!formData.recurrence_end_date) {
                        endDate.setMonth(endDate.getMonth() + 3);
                      }
                      return endDate.toLocaleDateString('fr-FR');
                    })()}
                  </p>
                </div>
              )}

              {/* Aperçu du temps estimé */}
              {formData.start_time && formData.end_time && (
                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                    ⏱️ Temps estimé: {calculateEstimatedHours(formData.start_time, formData.end_time, formData.entry_type).toFixed(2)}h
                  </p>
                  {['Travail', 'Télétravail'].includes(formData.entry_type) &&
                   calculateEstimatedHours(formData.start_time, formData.end_time, 'Événement') > 4 && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      (Pause de 45min déduite automatiquement car &gt; 4h)
                    </p>
                  )}
                  {formData.entry_type === 'Formation' &&
                   calculateEstimatedHours(formData.start_time, formData.end_time, 'Événement') > 4 && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      (Pause de 1h00 déduite automatiquement car &gt; 4h)
                    </p>
                  )}
                  {formData.entry_type === 'Récupération' && (
                    <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                      (Heures déduites de votre modulation)
                    </p>
                  )}
                  {formData.entry_type === 'Tâche personnel' && (
                    <p className="text-xs text-text-secondary mt-1">
                      (Non comptabilisé dans les heures de travail)
                    </p>
                  )}
                  {formData.entry_type === 'Repos' && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      (Jour de repos - non comptabilisé)
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Localisation</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Bureau, Site A, Visio, Domicile..."
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm"
                />
              </div>
              </div>

              {/* Footer sticky avec boutons */}
              <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingSchedule(null);
                    setFormData({
                      employee_id: '',
                      date: '',
                      start_time: '',
                      end_time: '',
                      entry_type: 'Travail',
                      location: '',
                      title: '',
                      description: '',
                      conge_type: '',
                      recuperation_hours: ''
                    });
                  }}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-center"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 text-center"
                >
                  {loading ? (editingSchedule ? 'Modification...' : 'Ajout en cours...') : (editingSchedule ? 'Modifier' : 'Ajouter au Planning')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Planning;
